import React, { useEffect, useState } from 'react';
import ssomaService from '../services/ssomaService';

export const TIPOS_SANGRE = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'];

const ESTADOS_PADRE_MADRE = [
  { value: '', label: 'Seleccionar…' },
  { value: 'vivo', label: 'Vivo(a)' },
  { value: 'fallecido', label: 'Fallecido(a)' },
];

const VACIO = {
  tipo_sangre: '',
  enfermedades_actuales: '',
  enfermedades_hereditarias: '',
  padre_estado: '',
  madre_estado: '',
};

const SsomaAntecedentesSaludTab = ({
  empresaId,
  empleadoId,
  compacto = false,
  soloLectura = false,
  requiereEditar = false,
}) => {
  const [form, setForm] = useState(VACIO);
  const [formInicial, setFormInicial] = useState(VACIO);
  const [editando, setEditando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const camposBloqueados = soloLectura || (requiereEditar && !editando);

  useEffect(() => {
    setEditando(false);
  }, [empleadoId, empresaId]);

  useEffect(() => {
    const cargar = async () => {
      if (!empresaId || !empleadoId) return;
      setCargando(true);
      setError('');
      const response = await ssomaService.obtenerAntecedentesSalud(empresaId, empleadoId);
      if (response.success) {
        const datos = {
          tipo_sangre: response.data?.tipo_sangre || '',
          enfermedades_actuales: response.data?.enfermedades_actuales || '',
          enfermedades_hereditarias: response.data?.enfermedades_hereditarias || '',
          padre_estado: response.data?.padre_estado || '',
          madre_estado: response.data?.madre_estado || '',
        };
        setForm(datos);
        setFormInicial(datos);
      } else {
        setError(response.error || 'No se pudieron cargar los antecedentes');
      }
      setCargando(false);
    };
    cargar();
  }, [empresaId, empleadoId]);

  const iniciarEdicion = () => {
    setFormInicial({ ...form });
    setEditando(true);
    setMensaje('');
    setError('');
  };

  const cancelarEdicion = () => {
    setForm({ ...formInicial });
    setEditando(false);
    setMensaje('');
    setError('');
  };

  const guardar = async () => {
    if (camposBloqueados || !empresaId || !empleadoId) return;
    setGuardando(true);
    setError('');
    setMensaje('');
    const response = await ssomaService.guardarAntecedentesSalud(empresaId, empleadoId, form);
    if (response.success) {
      setFormInicial({ ...form });
      if (requiereEditar) {
        setEditando(false);
      }
      setMensaje('Datos de salud guardados');
      setTimeout(() => setMensaje(''), 3000);
    } else {
      setError(response.error || 'No se pudieron guardar los datos');
    }
    setGuardando(false);
  };

  if (!empleadoId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-800">
        Guarda el empleado primero para registrar antecedentes de salud SSOMA.
      </div>
    );
  }

  if (cargando) {
    return <p className="text-sm text-gray-500">Cargando antecedentes de salud…</p>;
  }

  const inputClass = (extra = '') =>
    `w-full border rounded-lg px-3 py-2 text-sm ${
      camposBloqueados ? 'border-gray-200 bg-gray-50 text-gray-700' : 'border-gray-300'
    } ${extra}`;

  return (
    <div className="space-y-4">
      {requiereEditar && !soloLectura && (
        <div className="flex justify-end">
          {!editando ? (
            <button
              type="button"
              onClick={iniciarEdicion}
              className="px-3 py-1.5 text-sm font-medium text-teal-700 border border-teal-600 rounded-lg hover:bg-teal-50"
            >
              Editar
            </button>
          ) : (
            <button
              type="button"
              onClick={cancelarEdicion}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancelar
            </button>
          )}
        </div>
      )}
      {!compacto && !camposBloqueados && (
        <p className="text-sm text-gray-600">
          Tipo de sangre, enfermedades actuales y hereditarias, y estado de los padres (vivo o fallecido).
        </p>
      )}
      {soloLectura && (
        <p className="text-xs text-gray-500">Solo consulta (personal cesado).</p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}
      {mensaje && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{mensaje}</div>
      )}

      <div className={`grid grid-cols-1 ${compacto ? 'sm:grid-cols-3' : 'md:grid-cols-3'} gap-4`}>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Tipo de sangre</span>
          <select
            value={form.tipo_sangre}
            onChange={(e) => setForm({ ...form, tipo_sangre: e.target.value })}
            disabled={camposBloqueados}
            className={inputClass()}
          >
            <option value="">Seleccionar…</option>
            {TIPOS_SANGRE.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Padre</span>
          <select
            value={form.padre_estado}
            onChange={(e) => setForm({ ...form, padre_estado: e.target.value })}
            disabled={camposBloqueados}
            className={inputClass()}
          >
            {ESTADOS_PADRE_MADRE.map((o) => (
              <option key={`padre-${o.value}`} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Madre</span>
          <select
            value={form.madre_estado}
            onChange={(e) => setForm({ ...form, madre_estado: e.target.value })}
            disabled={camposBloqueados}
            className={inputClass()}
          >
            {ESTADOS_PADRE_MADRE.map((o) => (
              <option key={`madre-${o.value}`} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1">Enfermedades actuales</span>
        <textarea
          value={form.enfermedades_actuales}
          onChange={(e) => setForm({ ...form, enfermedades_actuales: e.target.value })}
          rows={compacto ? 2 : 3}
          readOnly={camposBloqueados}
          className={inputClass()}
          placeholder={camposBloqueados ? 'Sin registrar' : 'Ej. hipertensión controlada; ninguna'}
        />
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700 mb-1">Enfermedades hereditarias</span>
        <textarea
          value={form.enfermedades_hereditarias}
          onChange={(e) => setForm({ ...form, enfermedades_hereditarias: e.target.value })}
          rows={compacto ? 2 : 3}
          readOnly={camposBloqueados}
          className={inputClass()}
          placeholder={camposBloqueados ? 'Sin registrar' : 'Ej. diabetes en línea materna; ninguna'}
        />
      </label>

      {!camposBloqueados && (
        <button
          type="button"
          onClick={guardar}
          disabled={guardando}
          className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:bg-gray-400"
        >
          {guardando ? 'Guardando…' : 'Guardar antecedentes de salud'}
        </button>
      )}
    </div>
  );
};

export default SsomaAntecedentesSaludTab;
